import { onLanguageChange, t } from '../i18n';
import type { BetCashoutFeed, BetPlacedFeed, FeedEntry } from '../net/protocol';
import { formatAmount, formatMultiplier } from '../util/format';
import { avatarSvg } from './avatars';

/**
 * The live bet feed.
 *
 * Every name shown here was masked on the server. The client has no way to
 * recover the real one, which is the point: masking in the browser would mean
 * the real name had already crossed the wire.
 */
export class Feed {
  private readonly list: HTMLElement;
  private readonly count: HTMLElement;

  /**
   * Keyed by masked name *and slot*.
   *
   * A player may hold two stakes in one round, and they settle at different
   * multipliers. Keyed by name alone the second stake overwrote the first's row
   * and one of the two cash-outs vanished from the feed.
   */
  private rows = new Map<string, HTMLLIElement>();

  /** The last count shown, so a language change can rewrite "N players". */
  private lastCount = 0;

  constructor(private readonly scale: number) {
    this.list = must('feed-list');
    this.count = must('feed-count');
    onLanguageChange(() => this.refreshCount(this.lastCount));
  }

  reset(entries: FeedEntry[] = []): void {
    this.rows.clear();
    this.list.replaceChildren();
    // The server sends this list already ordered -- settled first, highest
    // multiplier down -- and each row is appended rather than prepended so that
    // order survives into the DOM.
    for (const e of entries) {
      const row = this.upsert(e.nickname, e.slot, e.amount, e.avatarId, { append: true });
      if (e.cashedOutX100 && e.payout) this.markWon(row, e.cashedOutX100, e.payout);
    }
    this.refreshCount();
  }

  onBetPlaced(msg: BetPlacedFeed): void {
    this.upsert(msg.nickname, msg.slot, msg.amount, msg.avatarId);
    this.refreshCount(msg.playerCount);
  }

  onCashout(msg: BetCashoutFeed): void {
    // A cash-out for a row we never saw placed can only happen after a reconnect
    // mid-round. Drawing it with avatar 0 would be a lie, so the row is created
    // without one and the next snapshot fills the list in properly.
    const row =
      this.rows.get(key(msg.nickname, msg.slot)) ??
      this.upsert(msg.nickname, msg.slot, '0', null);
    this.markWon(row, msg.multiplierX100, msg.payout);
  }

  private upsert(
    nickname: string,
    slot: number,
    amount: string,
    avatarId: number | null,
    opts: { append?: boolean } = {},
  ): HTMLLIElement {
    const rowKey = key(nickname, slot);
    const existing = this.rows.get(rowKey);
    if (existing) return existing;

    const li = document.createElement('li');

    if (avatarId === null) {
      // Keeps the four-column grid aligned when there is no drawing to show.
      li.appendChild(document.createElement('span'));
    } else {
      const avatar = avatarSvg(avatarId);
      avatar.classList.add('avatar');
      li.appendChild(avatar);
    }

    const who = document.createElement('span');
    who.className = 'who';
    who.textContent = nickname;

    const stake = document.createElement('span');
    stake.className = 'stake';
    stake.textContent = formatAmount(amount, this.scale);

    const mult = document.createElement('span');
    mult.className = 'mult pending';
    mult.textContent = '—';

    li.append(who, stake, mult);
    // The key travels with the row so trimming the list can forget it again.
    li.dataset['key'] = rowKey;

    // Newest first, so a player sees their own bet appear without scrolling --
    // except when replaying a list the server has already ordered.
    if (opts.append) this.list.appendChild(li);
    else this.list.insertBefore(li, this.firstUnsettled());
    this.rows.set(rowKey, li);

    while (this.list.children.length > 120) {
      const last = this.list.lastElementChild as HTMLLIElement | null;
      if (!last) break;
      const stale = last.dataset['key'];
      if (stale) this.rows.delete(stale);
      last.remove();
    }

    return li;
  }

  /**
   * Moves a row that has just cashed out up among the settled ones, highest
   * multiplier first.
   *
   * Without this the list stayed in arrival order and the interesting rows -- who
   * got out, and at what -- were scattered through it. The insertion point is
   * computed before anything moves, because iterating the children while
   * reparenting one of them is how a row ends up in two places.
   */
  private promote(row: HTMLLIElement, x100: number): void {
    row.dataset['x100'] = String(x100);

    let before: Element | null = null;
    for (const el of [...this.list.children]) {
      if (el === row) continue;
      const settled = (el as HTMLElement).dataset['x100'];
      if (settled === undefined || Number(settled) < x100) {
        before = el;
        break;
      }
    }
    if (row.nextElementSibling !== before || row.parentElement !== this.list) {
      this.list.insertBefore(row, before);
    }
  }

  /** The first row that has not cashed out, which is where a new bet belongs. */
  private firstUnsettled(): Element | null {
    for (const el of this.list.children) {
      if ((el as HTMLElement).dataset['x100'] === undefined) return el;
    }
    return null;
  }

  private markWon(row: HTMLLIElement, x100: number, payout: string): void {
    row.classList.add('won');
    const mult = row.querySelector('.mult');
    if (mult) {
      mult.classList.remove('pending');
      mult.textContent = formatMultiplier(x100);
    }
    const stake = row.querySelector('.stake');
    if (stake) stake.textContent = `+${formatAmount(payout, this.scale)}`;
    this.promote(row, x100);
  }

  /**
   * The count the server sends is distinct *players*, not rows: a player with
   * two stakes is one player and two rows, so it is never derived from the map.
   */
  private refreshCount(explicit?: number): void {
    const n = explicit ?? this.rows.size;
    this.lastCount = n;
    this.count.textContent = n > 0 ? t('feed.players', { count: n }) : '';
  }
}

function key(nickname: string, slot: number): string {
  return `${nickname}#${slot}`;
}

function must(id: string): HTMLElement {
  const el = document.getElementById(id);
  if (!el) throw new Error(`feed: #${id} is missing from the page`);
  return el;
}
